import { useInfiniteQuery } from '@tanstack/react-query';
import { Select, Spin, Typography } from 'antd';
import stockApiCall from 'apps/investcore/apiCalls/stock-products';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

// The stocks API caps page_size at 100, so symbols are searched on the server
// and further pages load as the dropdown is scrolled.
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;
const LOAD_MORE_THRESHOLD_PX = 32;

const SymbolSelect = ({
  value,
  onChange,
  onFirstSymbolLoaded = undefined,
  ...props
}) => {
  const [search, setSearch] = useState('');
  const debouncedSetSearch = useMemo(
    () => debounce(setSearch, SEARCH_DEBOUNCE_MS),
    []
  );
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [stockApiCall.list.queryKey, 'symbol-select', search],
    queryFn: ({ pageParam }) =>
      stockApiCall.list.queryFn({
        page: pageParam,
        page_size: PAGE_SIZE,
        // id breaks ties so offset paging stays stable.
        ordering: 'symbol,id',
        ...(search ? { search } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const { page, page_size, count } = lastPage?.paging || {};
      return page && page * page_size < count ? page + 1 : undefined;
    },
  });

  const stocks = useMemo(
    () => (data?.pages || []).flatMap(p => p?.data || []),
    [data]
  );

  const options = useMemo(() => {
    const seen = new Set();
    return stocks
      .filter(s => !seen.has(s.symbol) && seen.add(s.symbol))
      .map(s => ({ value: s.symbol, label: s.symbol, title: s.name }));
  }, [stocks]);

  const firstSymbol = !search ? stocks[0]?.symbol : undefined;
  useEffect(() => {
    if (firstSymbol) onFirstSymbolLoaded?.(firstSymbol);
  }, [firstSymbol]);

  const onPopupScroll = e => {
    const { scrollTop, offsetHeight, scrollHeight } = e.currentTarget;
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      scrollTop + offsetHeight >= scrollHeight - LOAD_MORE_THRESHOLD_PX
    ) {
      fetchNextPage();
    }
  };

  return (
    <Select
      showSearch
      filterOption={false}
      placeholder="Search symbol"
      value={value}
      onChange={onChange}
      onSearch={value => debouncedSetSearch(value.trim())}
      onDropdownVisibleChange={open => {
        if (!open) {
          debouncedSetSearch.cancel();
          setSearch('');
        }
      }}
      onPopupScroll={onPopupScroll}
      options={options}
      optionRender={option => (
        <span>
          <b>{option.data.value}</b>{' '}
          <Typography.Text type="secondary">
            {option.data.title}
          </Typography.Text>
        </span>
      )}
      loading={isFetching}
      notFoundContent={isFetching ? <Spin size="small" /> : undefined}
      dropdownRender={menu => (
        <>
          {menu}
          {isFetchingNextPage && (
            <div style={{ textAlign: 'center', padding: 4 }}>
              <Spin size="small" />
            </div>
          )}
        </>
      )}
      popupMatchSelectWidth={320}
      {...props}
    />
  );
};

export default SymbolSelect;
